import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { HttpClientService } from '../../shared/services/http-client/http-client.service';
import {
  RegistrarErroIntegracaoRequestDto,
  RequerimentoAdminDetalheDto,
  RequerimentoAdminItemDto,
  RequerimentoAdminQueryParams,
  SolicitarReenvioRequestDto,
  StatusRequerimentoDto,
} from '../api/models';

const ADMIN_REQUERIMENTOS_PATH = '/v1/admin/requerimentos';

/** Chamadas HTTP à API de Requerimento de Benefício (backoffice). O mapeamento para a tela fica no service de domínio. */
@Injectable({
  providedIn: 'root',
})
export class RequerimentoOnlineSolicitacaoApiService {
  constructor(private readonly httpClient: HttpClientService) {}

  /** Requerimentos enviados de todos os participantes, já filtrados; sem paginação (a tela ordena e pagina). */
  listarRequerimentos(params?: RequerimentoAdminQueryParams): Observable<RequerimentoAdminItemDto[]> {
    return this.httpClient.get<RequerimentoAdminItemDto[]>(ADMIN_REQUERIMENTOS_PATH, {
      params: this.toHttpParams(params),
      apiUrl: environment.apiRequerimentoBeneficioUrl,
    });
  }

  /** Códigos e rótulos de status da tela, na ordem da tela. */
  listarStatus(): Observable<StatusRequerimentoDto[]> {
    return this.httpClient.get<StatusRequerimentoDto[]>(`${ADMIN_REQUERIMENTOS_PATH}/status`, {
      apiUrl: environment.apiRequerimentoBeneficioUrl,
    });
  }

  /** Detalhe da solicitação: dados, documentos vigentes, histórico e as flags das ações. */
  obterRequerimento(id: number): Observable<RequerimentoAdminDetalheDto> {
    return this.httpClient.get<RequerimentoAdminDetalheDto>(`${ADMIN_REQUERIMENTOS_PATH}/${id}`, {
      apiUrl: environment.apiRequerimentoBeneficioUrl,
    });
  }

  /** Arquivo do documento (vigente ou substituído), com o content-type original. */
  obterArquivo(id: number, idDocumento: number): Observable<Blob> {
    return this.httpClient.getBlob(`${ADMIN_REQUERIMENTOS_PATH}/${id}/documentos/${idDocumento}/arquivo`, {
      apiUrl: environment.apiRequerimentoBeneficioUrl,
    });
  }

  /** Aprova os documentos pendentes; o requerimento vai a Documento Validado e segue para a integração. */
  aprovarDocumentos(id: number): Observable<RequerimentoAdminDetalheDto> {
    return this.postDetalhe(`${id}/documentos/aprovar`, {});
  }

  /** Recusa os documentos marcados (motivo por documento) e aprova os demais pendentes. */
  solicitarReenvio(id: number, body: SolicitarReenvioRequestDto): Observable<RequerimentoAdminDetalheDto> {
    return this.postDetalhe(`${id}/documentos/reenvio`, body);
  }

  /** "Aprovar solicitação": o processo foi concluído no corporativo (L → O). */
  concluirSolicitacao(id: number): Observable<RequerimentoAdminDetalheDto> {
    return this.postDetalhe(`${id}/integracao/concluir`, {});
  }

  /** "Registrar erro": o processo falhou no corporativo (L → F). */
  registrarErroIntegracao(id: number, body: RegistrarErroIntegracaoRequestDto): Observable<RequerimentoAdminDetalheDto> {
    return this.postDetalhe(`${id}/integracao/erro`, body);
  }

  /** "Reprocessar": envia a solicitação de novo ao corporativo. */
  reprocessarIntegracao(id: number): Observable<RequerimentoAdminDetalheDto> {
    return this.postDetalhe(`${id}/integracao/reprocessar`, {});
  }

  private postDetalhe(caminho: string, body: unknown): Observable<RequerimentoAdminDetalheDto> {
    return this.httpClient.post<RequerimentoAdminDetalheDto>(`${ADMIN_REQUERIMENTOS_PATH}/${caminho}`, body, {
      apiUrl: environment.apiRequerimentoBeneficioUrl,
    });
  }

  private toHttpParams(params: RequerimentoAdminQueryParams = {}): HttpParams {
    return Object.entries(params).reduce(
      (httpParams, [chave, valor]) =>
        valor == null || valor === '' ? httpParams : httpParams.set(chave, String(valor)),
      new HttpParams(),
    );
  }
}
